import { StatusEgressState } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { displayBitrateFromBytes, displayResolution } from '../output/use-outputting.js';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { DeviceEgressSelectorType } from '#~/statequery/selectors/device-egress.js';
import {
  AudioEgressCodec,
  VideoEgressCodec
} from '@tryiris-ai/common.protojs/api/v1/livekit_egress';
import type { Device } from '@studio/models/device';
import type { RTMPDestinationStatus } from '#~/statequery/selectors/device-rtmp-destinations.js';

export const getRTMPConfig = (
  source: DeviceEgressSelectorType,
  destinationState: RTMPDestinationStatus
) => {
  const { bps, duration: _sourceDuration, framerate, width, height } = source.egress?.rtmpStatus!;

  let duration = 0;
  if (destinationState?.duration) {
    duration = destinationState.duration;
  }

  return [
    {
      name: 'Duration',
      duration: duration || 'calculating...'
    },
    ...(source.type === 'video'
      ? [
          {
            name: 'Resolution',
            value: displayResolution(width ?? 0, height ?? 0)
          }
        ]
      : []),
    {
      name: 'Bitrate',
      value: displayBitrateFromBytes(bps ?? 0)
    },
    ...(source.type === 'video'
      ? [
          {
            name: 'FPS',
            value: Math.round(framerate ?? 0)
          }
        ]
      : [])
  ];
};

export const getErrorMessage = (
  state: T_Maybe<StatusEgressState>,
  context?: 'rtmp' | 'generic',
  error?: string | null
) => {
  // Prefer specific backend error when available (e.g., codec validation).
  if (
    error !== undefined &&
    error !== null &&
    (state === StatusEgressState.EGRESS_STATE_FAILED ||
      state === StatusEgressState.EGRESS_STATE_FAILED_TO_START)
  ) {
    return error;
  }

  const streamType = context === 'rtmp' ? 'RTMP streaming' : 'Streaming';

  if (state === StatusEgressState.EGRESS_STATE_FAILED) {
    return `${streamType} unexpectedly stopped. Check your connection and destination settings.`;
  }
  if (state === StatusEgressState.EGRESS_STATE_FAILED_TO_START) {
    return `${streamType} could not be started. Verify your RTMP server URL and stream key, then try again.`;
  }
  return undefined;
};

export enum AudioCodecs {
  DefaultAc = 'Default AC',
  Opus = 'Opus',
  AAC = 'AAC'
}

export const audioCodecsToEgressCodec: Record<AudioCodecs, AudioEgressCodec> = {
  [AudioCodecs.DefaultAc]: AudioEgressCodec.DEFAULT_AC,
  [AudioCodecs.Opus]: AudioEgressCodec.OPUS,
  [AudioCodecs.AAC]: AudioEgressCodec.AAC
};

export enum VideoCodecs {
  DefaultVc = 'Default VC',
  H264Base = 'H264 Baseline',
  H264Main = 'H264 Main',
  H264High = 'H264 High',
  VP8 = 'VP8'
}

export const videoCodecsToEgressCodec: Record<VideoCodecs, VideoEgressCodec> = {
  [VideoCodecs.DefaultVc]: VideoEgressCodec.DEFAULT_VC,
  [VideoCodecs.H264Base]: VideoEgressCodec.H264_BASELINE,
  [VideoCodecs.H264Main]: VideoEgressCodec.H264_MAIN,
  [VideoCodecs.H264High]: VideoEgressCodec.H264_HIGH,
  [VideoCodecs.VP8]: VideoEgressCodec.VP8
};

export interface ProfileConfig {
  deviceId: Device['id'];
  width: number;
  height: number;
  depth: number;
  frameRate: number;
  audioCodec: AudioCodecs;
  audioBitrate: number;
  audioFrequency: number;
  videoCodec: VideoCodecs;
  videoBitrate: number;
  keyFrameInterval: number;
}

/**
 * Maps stream profile value to actual profile configuration
 */
export const getProfileConfig = (profileValue: string, deviceId: Device['id']): ProfileConfig => {
  const baseConfig = {
    deviceId,
    depth: 24,
    audioCodec: AudioCodecs.Opus,
    audioBitrate: 128,
    audioFrequency: 44100,
    videoCodec: VideoCodecs.H264Main,
    keyFrameInterval: 4
  };

  switch (profileValue) {
    case '1080P-30FPS':
      return {
        ...baseConfig,
        width: 1920,
        height: 1080,
        frameRate: 30,
        videoBitrate: 6000
      };
    case '1080P-60FPS':
      return {
        ...baseConfig,
        width: 1920,
        height: 1080,
        frameRate: 60,
        videoBitrate: 9000
      };
    case '720P-30FPS':
      return {
        ...baseConfig,
        width: 1280,
        height: 720,
        frameRate: 30,
        videoBitrate: 3000
      };
    case '720P-60FPS':
      return {
        ...baseConfig,
        width: 1280,
        height: 720,
        frameRate: 60,
        videoBitrate: 4500
      };
    default:
      // Default to 1080P-30FPS
      return {
        ...baseConfig,
        width: 1920,
        height: 1080,
        frameRate: 30,
        videoBitrate: 6000
      };
  }
};
